/**
 * ItemGrid - item grid (for inventories)
 */

import React from 'react';
import { SPACE } from '../../theme';
import { ItemSlot } from './ItemSlot';
import { ItemData, ItemSize } from './shared';
import type { ItemTooltipViewerContext } from '../Tooltip';

export interface ItemGridProps {
  items: (ItemData | null | undefined)[];
  columns?: number;
  slotSize?: ItemSize;
  maxSlots?: number;
  selectedIndex?: number;
  onSlotClick?: (item: ItemData | null, index: number) => void;
  onSlotRightClick?: (item: ItemData | null, index: number) => void;
  onSlotDoubleClick?: (item: ItemData | null, index: number) => void;
  onSlotDragStart?: (event: React.DragEvent, item: ItemData, index: number) => void;
  onSlotDragEnd?: (event: React.DragEvent, item: ItemData, index: number) => void;
  isSlotDraggable?: (item: ItemData, index: number) => boolean;
  className?: string;
  /** Merged over the grid's default gap/padding (e.g. a compact grid in a narrow column). */
  style?: React.CSSProperties;
  viewer?: ItemTooltipViewerContext;
}

export const ItemGrid: React.FC<ItemGridProps> = ({
  items,
  columns = 5,
  slotSize = 'md',
  maxSlots = 50,
  selectedIndex,
  onSlotClick,
  onSlotRightClick,
  onSlotDoubleClick,
  onSlotDragStart,
  onSlotDragEnd,
  isSlotDraggable,
  className,
  style,
  viewer,
}) => {
  const slots = Array.from({ length: maxSlots }, (_, i) => items[i] || null);

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: SPACE.md,
    padding: SPACE.lg,
    ...style,
  };

  return (
    <div style={gridStyle} className={className}>
      {slots.map((item, index) => (
        <ItemSlot
          key={index}
          item={item || undefined}
          size={slotSize}
          selected={selectedIndex === index}
          onClick={() => onSlotClick?.(item, index)}
          onRightClick={() => onSlotRightClick?.(item, index)}
          onDoubleClick={() => onSlotDoubleClick?.(item, index)}
          draggable={!!item && (isSlotDraggable?.(item, index) ?? !!onSlotDragStart)}
          onDragStart={item ? (event) => onSlotDragStart?.(event, item, index) : undefined}
          onDragEnd={item ? (event) => onSlotDragEnd?.(event, item, index) : undefined}
          ariaLabel={item ? `${item.name} inventory slot` : `Empty inventory slot ${index + 1}`}
          viewer={viewer}
        />
      ))}
    </div>
  );
};
